export interface SocialDisadvantageFactor {
  key: string;
  letter: string;
  titleKey: string;
  tooltipKey: string;
}

export const SOCIAL_DISADVANTAGE_SCORES = [0, 1, 2, 4] as const;

export const SOCIAL_DISADVANTAGE_FACTORS: SocialDisadvantageFactor[] = [
  {
    key: 'homeSupport',
    letter: 'A',
    titleKey: 'child/socialDisadvantageFactorATitle',
    tooltipKey: 'child/socialDisadvantageFactorADescription',
  },
  {
    key: 'language',
    letter: 'B',
    titleKey: 'child/socialDisadvantageFactorBTitle',
    tooltipKey: 'child/socialDisadvantageFactorBDescription',
  },
  {
    key: 'behaviour',
    letter: 'C',
    titleKey: 'child/socialDisadvantageFactorCTitle',
    tooltipKey: 'child/socialDisadvantageFactorCDescription',
  },
  {
    key: 'attendance',
    letter: 'D',
    titleKey: 'child/socialDisadvantageFactorDTitle',
    tooltipKey: 'child/socialDisadvantageFactorDDescription',
  },
  {
    key: 'psychologicalNeeds',
    letter: 'E',
    titleKey: 'child/socialDisadvantageFactorETitle',
    tooltipKey: 'child/socialDisadvantageFactorEDescription',
  },
  {
    key: 'materialNeeds',
    letter: 'F',
    titleKey: 'child/socialDisadvantageFactorFTitle',
    tooltipKey: 'child/socialDisadvantageFactorFDescription',
  },
  {
    key: 'parentCooperation',
    letter: 'G',
    titleKey: 'child/socialDisadvantageFactorGTitle',
    tooltipKey: 'child/socialDisadvantageFactorGDescription',
  },
];

export type SocialDisadvantageScores = Record<string, number>;

export const SOCIAL_DISADVANTAGE_CODE_PATTERN = /^[0124]{7}$/;

export function buildSocialDisadvantageCode(scores: SocialDisadvantageScores): string {
  return SOCIAL_DISADVANTAGE_FACTORS.map((factor) => scores[factor.key] ?? 0).join('');
}

export function parseSocialDisadvantageCode(code: string | null | undefined): SocialDisadvantageScores | null {
  if (!code || !SOCIAL_DISADVANTAGE_CODE_PATTERN.test(code)) {
    return null;
  }
  const digits = code.split('').map(Number);
  return Object.fromEntries(SOCIAL_DISADVANTAGE_FACTORS.map((factor, index) => [factor.key, digits[index]]));
}

export type SocialDisadvantageCategoryKey = '0' | 'I' | 'II' | 'III';

export interface SocialDisadvantageCategory {
  key: SocialDisadvantageCategoryKey;
  labelKey: string;
  total: number;
}

const CATEGORY_LABEL_KEYS: Record<SocialDisadvantageCategoryKey, string> = {
  '0': 'child/socialDisadvantageCategory0',
  I: 'child/socialDisadvantageCategoryI',
  II: 'child/socialDisadvantageCategoryII',
  III: 'child/socialDisadvantageCategoryIII',
};

export function calculateSocialDisadvantageCategory(scores: SocialDisadvantageScores): SocialDisadvantageCategory {
  const values = SOCIAL_DISADVANTAGE_FACTORS.map((factor) => scores[factor.key] ?? 0);
  const total = values.reduce((sum, value) => sum + value, 0);
  const countOf = (score: number) => values.filter((value) => value === score).length;

  let key: SocialDisadvantageCategoryKey;
  if (countOf(4) >= 1) {
    key = 'III';
  } else if (countOf(2) >= 2 || total > 5) {
    key = 'II';
  } else if (countOf(2) === 1 || countOf(1) >= 4) {
    key = 'I';
  } else {
    key = '0';
  }

  return { key, labelKey: CATEGORY_LABEL_KEYS[key], total };
}
